import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useStatusWebSocket } from '@/hooks/useStatusWebSocket';
import {
  Task,
  CreateTaskRequest,
  UpdateTaskRequest,
  TASK_STATUS_CONFIG,
  TASK_PRIORITY_CONFIG,
  TASK_ACTOR_CONFIG,
  AGENT_TYPE_CONFIG,
  MANUAL_TASK_STATUSES,
  TaskPriority,
  TaskStatus,
  TaskActor,
  AgentAssignee,
  AssignableUser,
  TaskProject,
  TaskChatType,
  taskIdToNato,
  formatDuration,
  formatFileSize,
  TaskAttachment,
} from '../../types/tasks';
import {
  getTask,
  createTask,
  updateTask,
  deleteTask,
  archiveTask,
  unarchiveTask,
  addTaskDependency,
  removeTaskDependency,
  getTaskAttachments,
  uploadTaskAttachmentWithProgress,
  getAvailableAssignees,
  getAssignableUsers,
  getTaskFolder,
  copyDiskFileToTask,
  type AttachmentUploadProgress,
  type TaskFolderInfo,
} from '../../services/api';
import { taskProjectsApi } from '../../services/taskProjectsApi';
import { useTenant } from '../../contexts/TenantContext';
import EditableField from './EditableField';
import TaskSearchPanel from './TaskSearchPanel';
import FileUploader from './FileUploader';
import TaskActivityHistory from './TaskActivityHistory';
import TaskChatPanel from './TaskChatPanel';
import ReportSection from './ReportSection';
import DiskFilePickerModal from './DiskFilePickerModal';
import TaskFileSearch from './TaskFileSearch';
import UserAvatar from '../UserAvatar';
import {
  IterationList,
  NewIterationModal,
  CompleteIterationModal,
  ReportViewer,
} from './iterations';
import type { TaskIteration } from '../../types/tasks';
import { getTaskIterations, submitIteration } from '../../services/api';

// Default values for new task
const DEFAULT_NEW_TASK: Partial<Task> = {
  title: '',
  description: null,
  created_by: 'user',
  assigned_to: null,  // Legacy - use assigned_to_agent_id instead
  assigned_to_agent_id: null,  // Will be set when user selects from dropdown
  assigned_agent_name: null,
  assigned_agent_type: null,
  status: 'pending',
  priority: 'medium',
  due_date: null,
  estimated_minutes: null,
  actual_minutes: null,
  tags: [],
  metadata: {},
};

// Helper component for sidebar fields
const SidebarField: React.FC<{
  label: string;
  children: React.ReactNode;
  readOnly?: boolean;
}> = ({ label, children, readOnly }) => (
  <div className={readOnly ? 'opacity-75' : ''}>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <div>{children}</div>
  </div>
);

// Custom dropdown for user assignment with avatars
const UserAssignDropdown: React.FC<{
  users: AssignableUser[];
  selectedUserId: number | null;
  onChange: (userId: number | null) => void;
}> = ({ users, selectedUserId, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const selectedUser = users.find(u => u.user_id === selectedUserId) ?? null;

  const filtered = search
    ? users.filter(u =>
        u.full_name.toLowerCase().includes(search.toLowerCase()) ||
        u.username.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const select = (userId: number | null) => {
    onChange(userId);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm border border-gray-200 rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none bg-white text-left transition-colors"
      >
        {selectedUser ? (
          <>
            <UserAvatar
              user={{ id: selectedUser.user_id, full_name: selectedUser.full_name, avatar_url: selectedUser.avatar_url }}
              size="sm"
            />
            <span className="flex-1 truncate text-gray-900">{selectedUser.full_name}</span>
          </>
        ) : (
          <>
            <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 text-xs flex-shrink-0">?</span>
            <span className="flex-1 text-gray-400">Not assigned</span>
          </>
        )}
        <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Search */}
          {users.length > 4 && (
            <div className="p-2 border-b border-gray-100">
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
                autoFocus
                className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:border-blue-400 focus:outline-none"
              />
            </div>
          )}

          <div className="max-h-48 overflow-y-auto">
            {/* Unassign option */}
            <button
              type="button"
              onClick={() => select(null)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                !selectedUserId ? 'bg-blue-50' : ''
              }`}
            >
              <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 text-xs flex-shrink-0">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </span>
              <span className="text-gray-400">Not assigned</span>
            </button>

            {/* User options */}
            {filtered.map(u => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => select(u.user_id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                  selectedUserId === u.user_id ? 'bg-blue-50' : ''
                }`}
              >
                <UserAvatar
                  user={{ id: u.user_id, full_name: u.full_name, avatar_url: u.avatar_url }}
                  size="sm"
                />
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-gray-900 truncate">{u.full_name}</div>
                  <div className="text-xs text-gray-400 truncate">{u.username}</div>
                </div>
                {selectedUserId === u.user_id && (
                  <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}

            {filtered.length === 0 && search && (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">No users found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TaskDetailPage: React.FC = () => {
  const { t } = useTranslation('tasks');
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if we're in create mode
  const isCreateMode = taskId?.toLowerCase() === 'new';

  // Real-time status WebSocket (only need task updates here; iterations handled by IterationList)
  const { lastTaskUpdate } = useStatusWebSocket();

  const [task, setTask] = useState<Partial<Task> | null>(null);
  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [showDependencySearch, setShowDependencySearch] = useState(false);
  const [dependencyLoading, setDependencyLoading] = useState<string | null>(null);

  // Attachment state
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  // Cloud Disk integration state
  const [showDiskPicker, setShowDiskPicker] = useState(false);
  const [taskFolderInfo, setTaskFolderInfo] = useState<TaskFolderInfo | null>(null);
  const [copyingFromDisk, setCopyingFromDisk] = useState(false);
  const [pendingDiskFiles, setPendingDiskFiles] = useState<{ id: string; name: string }[]>([]);

  // Upload progress state for create mode
  const [uploadProgress, setUploadProgress] = useState<{
    currentFile: string;
    currentIndex: number;
    totalFiles: number;
    percentage: number;
    speed: number;
    estimatedTimeRemaining: number;
  } | null>(null);

  // Iteration state
  const [iterations, setIterations] = useState<TaskIteration[]>([]);
  const [iterationsLoading, setIterationsLoading] = useState(false);
  const [showNewIterationModal, setShowNewIterationModal] = useState(false);
  const [iterationToComplete, setIterationToComplete] = useState<TaskIteration | null>(null);
  const [showReportViewer, setShowReportViewer] = useState(false);
  const [reportViewerInitialId, setReportViewerInitialId] = useState<string | undefined>();

  // Track if iterations have been loaded to prevent duplicate fetches
  const [iterationsLoaded, setIterationsLoaded] = useState(false);

  // Agent assignees state (for unified agent assignment model)
  const [availableAssignees, setAvailableAssignees] = useState<AgentAssignee[]>([]);
  const [assigneesLoading, setAssigneesLoading] = useState(true);

  // Institutional features
  const { tenant } = useTenant();
  const isInstitutional = tenant?.tenant_type === 'institutional';
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [projects, setProjects] = useState<TaskProject[]>([]);
  const [activeChatTab, setActiveChatTab] = useState<TaskChatType>('discussion');

  // Fetch available assignees on mount
  useEffect(() => {
    const fetchAssignees = async () => {
      try {
        setAssigneesLoading(true);
        const assignees = await getAvailableAssignees();
        setAvailableAssignees(assignees);
      } catch (err) {
        console.error('Failed to load available assignees:', err);
      } finally {
        setAssigneesLoading(false);
      }
    };
    fetchAssignees();
  }, []);

  // Fetch institutional data (assignable users + projects)
  useEffect(() => {
    if (!isInstitutional) return;
    const fetchInstitutionalData = async () => {
      try {
        const [users, projs] = await Promise.all([
          getAssignableUsers(),
          taskProjectsApi.list(),
        ]);
        setAssignableUsers(users);
        setProjects(projs);
      } catch (err) {
        console.error('Failed to load institutional data:', err);
      }
    };
    fetchInstitutionalData();
  }, [isInstitutional]);

  // Initialize for create mode or load existing task
  const loadTask = useCallback(async () => {
    if (isCreateMode) {
      setTask({ ...DEFAULT_NEW_TASK });
      setLoading(false);
      return;
    }

    if (!taskId) return;

    setLoading(true);
    setError(null);

    // Check if task was passed via navigation state
    const stateTask = (location.state as { task?: Task })?.task;
    if (stateTask && stateTask.id.toUpperCase() === taskId.toUpperCase()) {
      setTask(stateTask);
      setLoading(false);
      return;
    }

    // Fetch from API
    try {
      const fetched = await getTask(taskId);
      setTask(fetched);
    } catch (err) {
      console.error('Failed to load task:', err);
      setError('Task not found');
    } finally {
      setLoading(false);
    }
  }, [taskId, location.state, isCreateMode]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  // Load attachments when task is loaded (edit mode only)
  useEffect(() => {
    if (!isCreateMode && taskId && task?.id) {
      setAttachmentsLoading(true);
      getTaskAttachments(taskId)
        .then(setAttachments)
        .catch((err) => console.error('Failed to load attachments:', err))
        .finally(() => setAttachmentsLoading(false));

      // Load task folder info for Cloud Disk integration
      getTaskFolder(taskId)
        .then(setTaskFolderInfo)
        .catch(() => setTaskFolderInfo(null));
    }
  }, [isCreateMode, taskId, task?.id]);

  // Load iterations when task is loaded (edit mode only)
  // Note: We only trigger this once when task is loaded, not on every task?.id change
  const loadIterations = useCallback(async () => {
    if (!isCreateMode && taskId) {
      setIterationsLoading(true);
      try {
        const response = await getTaskIterations(taskId, true);
        setIterations(response.iterations);
        setIterationsLoaded(true);
      } catch (err) {
        console.error('Failed to load iterations:', err);
      } finally {
        setIterationsLoading(false);
      }
    }
  }, [isCreateMode, taskId]);

  // Load iterations only once when task loads, not on every re-render
  useEffect(() => {
    if (!isCreateMode && taskId && task?.id && !iterationsLoaded) {
      loadIterations();
    }
  }, [isCreateMode, taskId, task?.id, iterationsLoaded, loadIterations]);

  // Memoized callback for iteration changes to prevent infinite loops
  const handleIterationsChange = useCallback(() => {
    loadIterations();
    loadTask();
  }, [loadIterations, loadTask]);

  // ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (deleteConfirm) {
          setDeleteConfirm(false);
        } else {
          navigate('/tasks');
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [navigate, deleteConfirm]);

  // Lock body scroll when detail page is open
  useEffect(() => {
    // Save current scroll position and lock scroll
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      // Restore scroll
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  // React to real-time task status updates from WebSocket
  useEffect(() => {
    if (lastTaskUpdate && task && lastTaskUpdate.id === task.id) {
      setTask(prev => prev ? {
        ...prev,
        status: lastTaskUpdate.status as Task['status'],
        updated_at: lastTaskUpdate.updated_at
      } : null);
    }
  }, [lastTaskUpdate, task?.id]);

  const handleClose = () => navigate('/tasks');

  // Handle field changes in create mode (local state only)
  const handleFieldChange = (
    field: keyof CreateTaskRequest,
    value: string | number | null | undefined
  ): void => {
    setTask(prev => prev ? { ...prev, [field]: value } : null);
  };

  // Handle field updates in edit mode (API call with optimistic update)
  const handleFieldUpdate = async (
    field: keyof UpdateTaskRequest,
    value: string | number | null | undefined
  ): Promise<void> => {
    if (!task) return;

    // In create mode, just update local state
    if (isCreateMode) {
      handleFieldChange(field as keyof CreateTaskRequest, value);
      return;
    }

    const previousTask = { ...task };

    // Optimistic update
    setTask(prev => prev ? { ...prev, [field]: value } : null);

    try {
      const updated = await updateTask(task.id as string, { [field]: value });
      setTask(updated);
    } catch (err) {
      // Rollback on error
      setTask(previousTask);
      throw err;
    }
  };

  // Handle creating a new task
  const handleCreateTask = async () => {
    if (!task) return;
    if (!task.title?.trim()) {
      setError(t('detail.errors.titleRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const createData: CreateTaskRequest = {
        title: task.title.trim(),
        description: task.description || undefined,
        created_by: task.created_by || 'user',
        assigned_to: task.assigned_to_agent_id ? undefined : (task.assigned_to || 'user'),  // Legacy fallback
        assigned_to_agent_id: task.assigned_to_agent_id || undefined,  // New unified model
        priority: task.priority || 'medium',
        due_date: task.due_date || undefined,
        estimated_minutes: task.estimated_minutes || undefined,
        tags: task.tags || [],
        // Institutional features
        assigned_to_user_id: task.assigned_to_user_id || undefined,
        project_id: task.project_id || undefined,
        due_time: task.due_time || undefined,
      };

      const created = await createTask(createData);

      // Upload pending files if any (using presigned URLs for large file support)
      if (pendingFiles.length > 0) {
        for (let i = 0; i < pendingFiles.length; i++) {
          const file = pendingFiles[i];
          try {
            await uploadTaskAttachmentWithProgress(
              created.id,
              file,
              (progress: AttachmentUploadProgress) => {
                setUploadProgress({
                  currentFile: file.name,
                  currentIndex: i + 1,
                  totalFiles: pendingFiles.length,
                  percentage: progress.percentage,
                  speed: progress.speed,
                  estimatedTimeRemaining: progress.estimatedTimeRemaining,
                });
              },
              undefined,  // No abort signal
              undefined   // No iteration_id (task-level attachment)
            );
          } catch (err) {
            console.error('Failed to upload attachment:', file.name, err);
            // Continue uploading other files even if one fails
          }
        }
        setUploadProgress(null);
      }

      // Copy pending disk files
      if (pendingDiskFiles.length > 0) {
        for (const df of pendingDiskFiles) {
          try {
            await copyDiskFileToTask(created.id, df.id);
          } catch (err) {
            console.error('Failed to copy disk file:', df.name, err);
          }
        }
      }

      // Submit the auto-created iteration (draft → pending, triggers upgrade_to_full)
      if (created.current_iteration_id && created.current_iteration_status === 'draft') {
        try {
          await submitIteration(created.id, created.current_iteration_id);
        } catch (err) {
          console.error('Failed to submit iteration:', err);
        }
      }

      // Navigate to the newly created task
      navigate(`/tasks/${created.id}`, { state: { task: created }, replace: true });
    } catch (err) {
      console.error('Failed to create task:', err);
      setError(t('detail.errors.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!task || !task.id) return;

    try {
      await deleteTask(task.id);
      navigate('/tasks');
    } catch (err) {
      console.error('Failed to delete task:', err);
      setError(t('detail.errors.deleteFailed'));
    }
  };

  const handleArchive = async () => {
    if (!task || !task.id) return;

    try {
      const updated = await archiveTask(task.id);
      setTask(updated);
      // Navigate back since archived tasks aren't in main list
      navigate('/tasks');
    } catch (err) {
      console.error('Failed to archive task:', err);
      setError(t('detail.errors.archiveFailed'));
    }
  };

  const handleUnarchive = async () => {
    if (!task || !task.id) return;

    try {
      const updated = await unarchiveTask(task.id);
      setTask(updated);
    } catch (err) {
      console.error('Failed to unarchive task:', err);
      setError(t('detail.errors.unarchiveFailed'));
    }
  };

  const handleStartWorking = async () => {
    if (!task || !task.id) return;

    try {
      await handleFieldUpdate('status', 'in_progress');
    } catch (err: unknown) {
      console.error('Failed to start task:', err);
      const errorMessage = err instanceof Error ? err.message : t('detail.errors.startFailed');
      setError(errorMessage);
    }
  };

  const handleAddDependency = async (dependsOnTask: Task) => {
    if (!task || !task.id) return;

    setDependencyLoading(dependsOnTask.id);
    try {
      await addTaskDependency(task.id, {
        depends_on_task_id: dependsOnTask.id,
        dependency_type: 'blocks',
      });
      // Reload task to get updated dependencies
      const updated = await getTask(task.id);
      setTask(updated);
      setShowDependencySearch(false);
    } catch (err) {
      console.error('Failed to add dependency:', err);
      setError(t('detail.errors.dependencyAddFailed'));
    } finally {
      setDependencyLoading(null);
    }
  };

  const handleRemoveDependency = async (dependencyId: string) => {
    if (!task || !task.id) return;

    setDependencyLoading(dependencyId);
    try {
      await removeTaskDependency(task.id, dependencyId);
      // Reload task to get updated dependencies
      const updated = await getTask(task.id);
      setTask(updated);
    } catch (err) {
      console.error('Failed to remove dependency:', err);
      setError(t('detail.errors.dependencyRemoveFailed'));
    } finally {
      setDependencyLoading(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-gray-500">{t('detail.loading')}</p>
        </div>
      </div>
    );
  }

  // Error / Not found state (only for edit mode - in create mode we have default task)
  if (!isCreateMode && (error || !task)) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">404</div>
          <h2 className="text-xl font-semibold mb-2">{t('detail.notFound.title')}</h2>
          <p className="text-gray-500 mb-6">
            {t('detail.notFound.description', { id: taskId?.toUpperCase() })}
          </p>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('detail.notFound.button')}
          </button>
        </div>
      </div>
    );
  }

  // Handle create mode task init failure (should not happen normally)
  if (!task) {
    return null;
  }

  // Archive is available for completed/blocked tasks (not for in_progress or open)
  // Unarchive is available for archived tasks
  const isArchived = task.status === 'archived';
  const canArchive = !isArchived && (task.status === 'completed' || task.status === 'blocked');

  // Start Working is available for 'open' tasks assigned to self (not AI)
  const canStartWorking = task.status === 'open' && task.assigned_agent_type === 'self';

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-hidden flex flex-col">
      {/* Header Bar */}
      <div className="flex-shrink-0 h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-3">
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('detail.header.close')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {isCreateMode ? (
            <span className="font-semibold text-gray-900">{t('detail.header.createTitle')}</span>
          ) : (
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-medium text-gray-700">{task.id}</span>
              <span className="text-xs text-gray-400 hidden sm:inline">
                {taskIdToNato(task.id as string)}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isCreateMode ? (
            <>
              <button
                onClick={handleClose}
                className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('detail.header.cancel')}
              </button>
              <button
                onClick={handleCreateTask}
                disabled={saving || !task.title?.trim()}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('detail.header.creating')}
                  </>
                ) : (
                  t('detail.header.createTask')
                )}
              </button>
            </>
          ) : (
            <>
              {canStartWorking && (
                <button
                  onClick={handleStartWorking}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors"
                  title={t('detail.header.startWorkingTitle')}
                >
                  {t('detail.header.startWorking')}
                </button>
              )}
              {isArchived && (
                <button
                  onClick={handleUnarchive}
                  className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title={t('detail.header.unarchiveTitle')}
                >
                  {t('detail.header.unarchive')}
                </button>
              )}
              {canArchive && (
                <button
                  onClick={handleArchive}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title={t('detail.header.archiveTitle')}
                >
                  {t('detail.header.archive')}
                </button>
              )}
              {deleteConfirm ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-red-600">{t('detail.header.deleteConfirm')}</span>
                  <button
                    onClick={handleDelete}
                    className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    {t('detail.header.yes')}
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(false)}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {t('detail.header.no')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setDeleteConfirm(true)}
                  className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={t('detail.header.deleteTitle')}
                >
                  {t('detail.header.delete')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Left Column: Main Content */}
            <div className="flex-1 min-w-0 order-2 lg:order-1">
              {/* Title */}
              <EditableField
                type="text"
                value={task.title}
                onSave={(v) => handleFieldUpdate('title', v)}
                className="text-2xl font-semibold text-gray-900"
                placeholder={t('detail.fields.title')}
                emptyText={t('detail.fields.titleEmpty')}
              />

              {/* Description */}
              <div className="mt-6">
                <label className="text-sm font-medium text-gray-500 mb-2 block">{t('detail.fields.description')}</label>
                <EditableField
                  type="textarea"
                  value={task.description || ''}
                  onSave={(v) => handleFieldUpdate('description', v || null)}
                  placeholder={t('detail.fields.descriptionPlaceholder')}
                  emptyText={t('detail.fields.descriptionEmpty')}
                  rows={6}
                />
              </div>

              {/* Input Files Section - only show input attachments (outputs are shown in iteration cards) */}
              <div className="mt-6">
                {(() => {
                  // Filter to only show input files (artifact_type === 'input' or not set for backwards compatibility)
                  const inputAttachments = attachments.filter(a => !a.artifact_type || a.artifact_type === 'input');
                  return (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-500">
                          {t('detail.fields.inputFiles')}
                          {(inputAttachments.length > 0 || pendingFiles.length > 0 || pendingDiskFiles.length > 0) && (
                            <span className="ml-1 text-gray-400">
                              ({inputAttachments.length + pendingFiles.length + pendingDiskFiles.length})
                            </span>
                          )}
                        </label>
                        <div className="flex items-center gap-2">
                          {!isCreateMode && taskFolderInfo?.folder_id && (
                            <button
                              onClick={() => navigate(`/disk?folder=${taskFolderInfo.folder_id}`)}
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              title={t('detail.viewInDiskTitle', 'View task files in Cloud Disk')}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                              </svg>
                              {t('detail.viewInDisk', 'View in Disk')}
                            </button>
                          )}
                          <button
                            onClick={() => setShowDiskPicker(true)}
                            disabled={copyingFromDisk}
                            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            {t('detail.attachFromDisk', 'Attach from Disk')}
                          </button>
                        </div>
                      </div>
                      {attachmentsLoading ? (
                        <div className="flex items-center gap-2 text-gray-500 py-4">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                          <span className="text-sm">{t('detail.fields.loadingAttachments')}</span>
                        </div>
                      ) : (
                        <>
                          <FileUploader
                            taskId={isCreateMode ? undefined : (task.id as string)}
                            attachments={inputAttachments}
                            onAttachmentsChange={(newAttachments) => {
                              // Preserve output attachments when input attachments change
                              const outputAttachments = attachments.filter(a => a.artifact_type === 'output');
                              setAttachments([...newAttachments, ...outputAttachments]);
                            }}
                            onPendingFilesChange={setPendingFiles}
                            disabled={saving}
                          />
                          {/* Pending disk files list (create mode only) */}
                          {isCreateMode && pendingDiskFiles.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {pendingDiskFiles.map((df) => (
                                <div
                                  key={df.id}
                                  className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-lg text-sm"
                                >
                                  <svg className="w-4 h-4 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                  </svg>
                                  <span className="flex-1 truncate text-gray-700">{df.name}</span>
                                  <button
                                    onClick={() => setPendingDiskFiles(prev => prev.filter(f => f.id !== df.id))}
                                    className="p-0.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                                    title={t('detail.removePendingFile', 'Remove')}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Iterations Section (only in edit mode, hide for non-AI tasks with no iterations) */}
              {!isCreateMode && task.id && (task.assigned_agent_type === 'ai' || iterations.length > 0) && (
                <div className="mt-6">
                  {iterationsLoading ? (
                    <div className="flex items-center gap-2 text-gray-500 py-4">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                      <span className="text-sm">{t('iterations.loading')}</span>
                    </div>
                  ) : (
                    <IterationList
                      taskId={task.id}
                      hasUnreadIterations={task.has_unread_iterations}
                      onNewIteration={() => setShowNewIterationModal(true)}
                      onEditIteration={(iteration) => {
                        // For now, just open in complete modal with existing data
                        setIterationToComplete(iteration);
                      }}
                      onCompleteIteration={(iteration) => setIterationToComplete(iteration)}
                      onViewReport={(iteration) => {
                        setReportViewerInitialId(iteration.id);
                        setShowReportViewer(true);
                      }}
                      onIterationsChange={handleIterationsChange}
                    />
                  )}
                </div>
              )}

              {/* Task File Search */}
              {!isCreateMode && task?.id && (
                <TaskFileSearch taskId={task.id} />
              )}

              {/* Dependencies Section - Editable (only in edit mode) */}
              {!isCreateMode && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-500">
                      {t('detail.fields.dependencies')}
                      {task.dependencies && task.dependencies.length > 0 && (
                        <span className="ml-1 text-gray-400">({task.dependencies.length})</span>
                      )}
                    </label>
                    <button
                      onClick={() => setShowDependencySearch(true)}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('detail.fields.dependenciesAdd')}
                    </button>
                  </div>

                  {task.dependencies && task.dependencies.length > 0 ? (
                    <div className="space-y-2">
                      {task.dependencies.map((dep) => (
                        <div
                          key={dep.id}
                          className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg group"
                        >
                          <button
                            onClick={() => navigate(`/tasks/${dep.depends_on_task_id}`, { state: {} })}
                            className="flex-1 text-left hover:bg-gray-100 rounded px-2 py-1 transition-colors"
                          >
                            <span className="font-mono text-sm text-gray-700">{dep.depends_on_task_id}</span>
                            {dep.depends_on_title && (
                              <span className="ml-2 text-sm text-gray-500">
                                {dep.depends_on_title.slice(0, 40)}
                                {dep.depends_on_title.length > 40 && '...'}
                              </span>
                            )}
                            {dep.depends_on_status && (
                              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${TASK_STATUS_CONFIG[dep.depends_on_status].bgColor} ${TASK_STATUS_CONFIG[dep.depends_on_status].color}`}>
                                {t(`status.${dep.depends_on_status}`)}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => handleRemoveDependency(dep.id)}
                            disabled={dependencyLoading === dep.id}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
                            title={t('detail.section.removeDependency')}
                          >
                            {dependencyLoading === dep.id ? (
                              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 italic py-2">
                      {t('detail.fields.dependenciesNone')}
                    </div>
                  )}
                </div>
              )}

              {/* Dependents Info (only in edit mode) */}
              {!isCreateMode && task.dependents && task.dependents.length > 0 && (
                <div className="mt-6">
                  <label className="text-sm font-medium text-gray-500 mb-2 block">{t('detail.fields.blockedBy')}</label>
                  <div className="flex flex-wrap gap-2">
                    {task.dependents.map((dep) => (
                      <button
                        key={dep.id}
                        onClick={() => navigate(`/tasks/${dep.task_id}`, { state: {} })}
                        className="px-2 py-1 bg-orange-100 hover:bg-orange-200 rounded text-sm font-mono transition-colors"
                      >
                        {dep.task_id}
                        {dep.title && (
                          <span className="ml-1 text-gray-500 font-sans">
                            - {dep.title.slice(0, 30)}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Blocked Warning (only in edit mode) */}
              {!isCreateMode && task.is_blocked && task.blocking_tasks && task.blocking_tasks.length > 0 && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                    <span>{t('detail.fields.blockedWarning')}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {task.blocking_tasks.map((blockingId) => (
                      <button
                        key={blockingId}
                        onClick={() => navigate(`/tasks/${blockingId}`, { state: {} })}
                        className="px-2 py-1 bg-red-100 hover:bg-red-200 rounded text-sm font-mono text-red-800 transition-colors"
                      >
                        {blockingId}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Report Section (institutional, non-AI tasks only) */}
              {isInstitutional && !isCreateMode && task.assigned_agent_type !== 'ai' && (
                <div className="mt-6">
                  <ReportSection
                    taskId={task.id as string}
                    reportText={task.report_text || null}
                    onSave={(text) => handleFieldUpdate('report_text', text)}
                  />
                </div>
              )}

              {/* Activity History (institutional only) */}
              {isInstitutional && !isCreateMode && task.id && (
                <div className="mt-6">
                  <TaskActivityHistory taskId={task.id as string} users={assignableUsers} />
                </div>
              )}

              {/* Chat Tabs (institutional only) */}
              {isInstitutional && !isCreateMode && task.id && (
                <div className="mt-6">
                  <div className="flex border-b border-gray-200 mb-3">
                    <button
                      type="button"
                      onClick={() => setActiveChatTab('author_notes')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeChatTab === 'author_notes'
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Author Notes
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveChatTab('discussion')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeChatTab === 'discussion'
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Discussion
                    </button>
                  </div>
                  <TaskChatPanel
                    taskId={task.id as string}
                    chatType={activeChatTab}
                    assignableUsers={assignableUsers}
                  />
                </div>
              )}
            </div>

            {/* Right Column: Sidebar */}
            <div className="w-full lg:w-72 flex-shrink-0 order-1 lg:order-2">
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                {/* Status - editable for non-AI tasks, read-only for AI tasks */}
                {!isCreateMode && task.status && (
                  <SidebarField label={t('detail.sidebar.status')} readOnly={task.assigned_agent_type === 'ai'}>
                    {task.assigned_agent_type === 'ai' ? (
                      <>
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${
                          TASK_STATUS_CONFIG[task.status]?.bgColor || 'bg-gray-100'
                        } ${TASK_STATUS_CONFIG[task.status]?.color || 'text-gray-700'}`}>
                          <span>{TASK_STATUS_CONFIG[task.status]?.icon}</span>
                          <span>{t(`status.${task.status}`)}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {t('detail.sidebar.statusComputed')}
                        </p>
                      </>
                    ) : (
                      <EditableField
                        type="select"
                        value={task.status}
                        onSave={(v) => handleFieldUpdate('status', v as TaskStatus)}
                        options={MANUAL_TASK_STATUSES.map((s) => ({
                          value: s,
                          label: t(`status.${s}`),
                        }))}
                      />
                    )}
                  </SidebarField>
                )}

                {/* Priority */}
                <SidebarField label={t('detail.sidebar.priority')}>
                  <EditableField
                    type="select"
                    value={task.priority}
                    onSave={(v) => handleFieldUpdate('priority', v as TaskPriority)}
                    options={Object.keys(TASK_PRIORITY_CONFIG).map((k) => ({
                      value: k,
                      label: t(`priority.${k}`),
                    }))}
                  />
                </SidebarField>

                {/* Assigned To — institutional: custom user dropdown with avatars; regular: agent dropdown */}
                {isInstitutional && assignableUsers.length > 0 ? (
                  <SidebarField label={t('detail.sidebar.assignedTo')}>
                    <UserAssignDropdown
                      users={assignableUsers}
                      selectedUserId={task.assigned_to_user_id ?? null}
                      onChange={(userId) => {
                        const selectedUser = assignableUsers.find(u => u.user_id === userId);
                        if (isCreateMode) {
                          setTask(prev => prev ? {
                            ...prev,
                            assigned_to_user_id: userId,
                            assigned_user_full_name: selectedUser?.full_name || null,
                            assigned_user_avatar_url: selectedUser?.avatar_url || null,
                          } : null);
                        } else {
                          handleFieldUpdate('assigned_to_user_id', userId);
                        }
                      }}
                    />
                  </SidebarField>
                ) : (
                  <SidebarField label={t('detail.sidebar.assignedTo')}>
                    {assigneesLoading ? (
                      <div className="text-sm text-gray-400">{t('detail.sidebar.assignedToLoading')}</div>
                    ) : availableAssignees.length > 0 ? (
                      <EditableField
                        type="select"
                        value={task.assigned_to_agent_id?.toString() || ''}
                        onSave={async (v) => {
                          const agentId = v ? parseInt(v, 10) : null;
                          if (isCreateMode) {
                            setTask(prev => prev ? {
                              ...prev,
                              assigned_to_agent_id: agentId,
                              assigned_agent_name: availableAssignees.find(a => a.agent_id === agentId)?.agent_name || null,
                              assigned_agent_type: availableAssignees.find(a => a.agent_id === agentId)?.agent_type || null,
                            } : null);
                          } else {
                            const previousTask = { ...task };
                            setTask(prev => prev ? {
                              ...prev,
                              assigned_to_agent_id: agentId,
                              assigned_agent_name: availableAssignees.find(a => a.agent_id === agentId)?.agent_name || null,
                              assigned_agent_type: availableAssignees.find(a => a.agent_id === agentId)?.agent_type || null,
                            } : null);
                            try {
                              const updated = await updateTask(task.id as string, { assigned_to_agent_id: agentId || undefined });
                              setTask(updated);
                            } catch (err) {
                              setTask(previousTask);
                              throw err;
                            }
                          }
                        }}
                        options={availableAssignees.map((a) => {
                          const ECONOMY_AGENTS = ['Scout', 'Economy Agent', 'Premium Economy Agent', 'Low-Cost Agent'];
                          const isEconomy = ECONOMY_AGENTS.some(n => a.agent_name.includes(n));
                          return {
                            value: a.agent_id.toString(),
                            label: a.is_self_agent ? t('detail.sidebar.assignedToMe') : a.agent_name,
                            icon: AGENT_TYPE_CONFIG[a.agent_type]?.icon || '👤',
                            group: a.is_self_agent ? undefined : isEconomy ? '💰 Economy Class' : '✈️ Business Class',
                          };
                        })}
                      />
                    ) : (
                      <EditableField
                        type="select"
                        value={task.assigned_to}
                        onSave={(v) => handleFieldUpdate('assigned_to', v as TaskActor)}
                        options={Object.keys(TASK_ACTOR_CONFIG).map((k) => ({
                          value: k,
                          label: t(`actor.${k}`),
                          icon: TASK_ACTOR_CONFIG[k as TaskActor].icon,
                        }))}
                      />
                    )}
                  </SidebarField>
                )}

                {/* Due Date */}
                <SidebarField label={t('detail.sidebar.dueDate')}>
                  <EditableField
                    type="date"
                    value={task.due_date?.split('T')[0] || ''}
                    onSave={(v) => handleFieldUpdate('due_date', v || null)}
                    placeholder={t('detail.sidebar.dueDatePlaceholder')}
                    emptyText={t('detail.sidebar.dueDateNone')}
                  />
                </SidebarField>

                {/* Due Time (institutional only, shown when due_date is set) */}
                {isInstitutional && task.due_date && (
                  <SidebarField label="Due Time">
                    <input
                      type="time"
                      value={task.due_time || ''}
                      onChange={(e) => {
                        const val = e.target.value || null;
                        if (isCreateMode) {
                          setTask(prev => prev ? { ...prev, due_time: val } : null);
                        } else {
                          handleFieldUpdate('due_time', val);
                        }
                      }}
                      className="w-full px-2 py-1 text-sm border border-gray-200 rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none"
                    />
                  </SidebarField>
                )}

                {/* Project (institutional only) */}
                {isInstitutional && projects.length > 0 && (
                  <SidebarField label="Project">
                    <select
                      value={task.project_id || ''}
                      onChange={(e) => {
                        const projectId = e.target.value || null;
                        if (isCreateMode) {
                          setTask(prev => prev ? { ...prev, project_id: projectId } : null);
                        } else {
                          handleFieldUpdate('project_id', projectId);
                        }
                      }}
                      className="w-full px-2 py-1 text-sm border border-gray-200 rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none"
                    >
                      <option value="">No project</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.icon} {p.name}
                        </option>
                      ))}
                    </select>
                  </SidebarField>
                )}

                {/* Estimated Minutes */}
                <SidebarField label={t('detail.sidebar.estimate')}>
                  <EditableField
                    type="number"
                    value={task.estimated_minutes ?? ''}
                    onSave={(v) => handleFieldUpdate('estimated_minutes', v ? parseInt(v, 10) : null)}
                    placeholder={t('detail.sidebar.estimatePlaceholder')}
                    suffix="min"
                    emptyText={t('detail.sidebar.estimateNone')}
                    min={0}
                  />
                </SidebarField>

                {/* Actual Minutes */}
                <SidebarField label={t('detail.sidebar.actualTime')}>
                  <EditableField
                    type="number"
                    value={task.actual_minutes ?? ''}
                    onSave={(v) => handleFieldUpdate('actual_minutes', v ? parseInt(v, 10) : null)}
                    placeholder={t('detail.sidebar.actualTimePlaceholder')}
                    suffix="min"
                    emptyText={t('detail.sidebar.actualTimeNone')}
                    min={0}
                  />
                </SidebarField>

                {/* Read-only fields - only in edit mode */}
                {!isCreateMode && (
                  <>
                    <hr className="border-gray-200" />

                    <SidebarField label={t('detail.sidebar.createdBy')} readOnly>
                      {(() => {
                        // For institutional tenants, show actual creator user with avatar
                        if (isInstitutional && task.user_id) {
                          const creatorUser = assignableUsers.find(u => u.user_id === task.user_id);
                          if (creatorUser) {
                            return (
                              <div className="flex items-center gap-2">
                                <UserAvatar
                                  user={{
                                    id: creatorUser.user_id,
                                    full_name: creatorUser.full_name,
                                    avatar_url: creatorUser.avatar_url,
                                  }}
                                  size="sm"
                                />
                                <span className="text-sm text-gray-900">{creatorUser.full_name}</span>
                              </div>
                            );
                          }
                        }
                        // Fallback: show actor type
                        const actorKey = task.created_by as TaskActor;
                        const config = TASK_ACTOR_CONFIG[actorKey] || TASK_ACTOR_CONFIG.user;
                        return (
                          <span className="text-sm flex items-center gap-1">
                            <span>{config.icon}</span>
                            <span>{t(`actor.${task.created_by}`)}</span>
                          </span>
                        );
                      })()}
                    </SidebarField>

                    <SidebarField label={t('detail.sidebar.created')} readOnly>
                      <span className="text-sm text-gray-600">
                        {new Date(task.created_at as string).toLocaleString()}
                      </span>
                    </SidebarField>

                    <SidebarField label={t('detail.sidebar.updated')} readOnly>
                      <span className="text-sm text-gray-600">
                        {new Date(task.updated_at as string).toLocaleString()}
                      </span>
                    </SidebarField>

                    {task.started_at && (
                      <SidebarField label={t('detail.sidebar.started')} readOnly>
                        <span className="text-sm text-gray-600">
                          {new Date(task.started_at).toLocaleString()}
                        </span>
                      </SidebarField>
                    )}

                    {task.completed_at && (
                      <SidebarField label={t('detail.sidebar.completed')} readOnly>
                        <span className="text-sm text-gray-600">
                          {new Date(task.completed_at).toLocaleString()}
                        </span>
                      </SidebarField>
                    )}
                  </>
                )}

                {/* Duration Summary */}
                {(task.estimated_minutes || task.actual_minutes) && (
                  <>
                    <hr className="border-gray-200" />
                    <div className="text-sm text-gray-600">
                      {task.estimated_minutes && task.actual_minutes ? (
                        <div className="flex justify-between">
                          <span>{t('detail.sidebar.progress')}</span>
                          <span className={
                            task.actual_minutes > task.estimated_minutes
                              ? 'text-red-600 font-medium'
                              : 'text-green-600'
                          }>
                            {formatDuration(task.actual_minutes)} / {formatDuration(task.estimated_minutes)}
                          </span>
                        </div>
                      ) : task.estimated_minutes ? (
                        <div className="flex justify-between">
                          <span>{t('detail.sidebar.estimated')}</span>
                          <span>{formatDuration(task.estimated_minutes)}</span>
                        </div>
                      ) : (
                        <div className="flex justify-between">
                          <span>{t('detail.sidebar.tracked')}</span>
                          <span>{formatDuration(task.actual_minutes!)}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Tags */}
                {task.tags && task.tags.length > 0 && (
                  <>
                    <hr className="border-gray-200" />
                    <SidebarField label={t('detail.sidebar.tags')} readOnly>
                      <div className="flex flex-wrap gap-1">
                        {task.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 bg-gray-200 rounded text-xs text-gray-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </SidebarField>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Toast for Create Mode */}
      {isCreateMode && error && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-60 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-2 hover:bg-red-700 rounded p-0.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Upload Progress Overlay for Create Mode */}
      {uploadProgress && (
        <div className="fixed inset-0 z-60 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('detail.upload.title', { current: uploadProgress.currentIndex, total: uploadProgress.totalFiles })}
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📎</span>
                <span className="text-sm text-gray-700 truncate flex-1">
                  {uploadProgress.currentFile}
                </span>
              </div>
              {/* Progress bar */}
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress.percentage}%` }}
                  />
                </div>
                <span className="text-sm text-blue-600 font-medium w-12 text-right">
                  {uploadProgress.percentage}%
                </span>
              </div>
              {/* Speed and ETA */}
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  {uploadProgress.speed > 0
                    ? `${formatFileSize(uploadProgress.speed)}/s`
                    : t('detail.upload.starting')}
                </span>
                {uploadProgress.estimatedTimeRemaining > 0 && (
                  <span>
                    {uploadProgress.estimatedTimeRemaining < 60
                      ? t('detail.upload.remaining', { seconds: uploadProgress.estimatedTimeRemaining })
                      : t('detail.upload.remainingMin', { minutes: Math.ceil(uploadProgress.estimatedTimeRemaining / 60) })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dependency Search Panel (only in edit mode) */}
      {!isCreateMode && showDependencySearch && (
        <TaskSearchPanel
          onSelect={handleAddDependency}
          onClose={() => setShowDependencySearch(false)}
          excludeTaskIds={[
            task.id as string,
            ...(task.dependencies?.map(d => d.depends_on_task_id) || []),
          ]}
          title={t('detail.section.addDependency')}
          placeholder={t('search.placeholderCustom')}
        />
      )}

      {/* Iteration Modals */}
      {!isCreateMode && task?.id && (
        <>
          <NewIterationModal
            taskId={task.id}
            isOpen={showNewIterationModal}
            onClose={() => setShowNewIterationModal(false)}
            onCreated={() => {
              setShowNewIterationModal(false);
              loadIterations();
            }}
          />

          {iterationToComplete && (
            <CompleteIterationModal
              taskId={task.id}
              iteration={iterationToComplete}
              isOpen={!!iterationToComplete}
              onClose={() => setIterationToComplete(null)}
              onCompleted={() => {
                setIterationToComplete(null);
                loadIterations();
              }}
            />
          )}

          <ReportViewer
            taskId={task.id}
            iterations={iterations}
            initialIterationId={reportViewerInitialId}
            isOpen={showReportViewer}
            onClose={() => {
              setShowReportViewer(false);
              setReportViewerInitialId(undefined);
            }}
          />
        </>
      )}

      {/* Disk File Picker Modal */}
      {showDiskPicker && (
        <DiskFilePickerModal
          isOpen={showDiskPicker}
          taskId={task?.id || ''}
          onClose={() => setShowDiskPicker(false)}
          onFilesCopied={async (files: { id: string; name: string }[]) => {
            if (isCreateMode) {
              // In create mode, store pending disk files for later copy
              setPendingDiskFiles(prev => {
                const existingIds = new Set(prev.map(f => f.id));
                const newFiles = files.filter(f => !existingIds.has(f.id));
                return [...prev, ...newFiles];
              });
            } else {
              // In edit mode, copy immediately
              const tid = task!.id as string;
              setCopyingFromDisk(true);
              try {
                for (const file of files) {
                  await copyDiskFileToTask(tid, file.id);
                }
                // Reload attachments
                const updated = await getTaskAttachments(tid);
                setAttachments(updated);
                // Reload folder info
                getTaskFolder(tid).then(setTaskFolderInfo).catch(() => {});
              } catch (err) {
                console.error('Failed to copy files from disk:', err);
              } finally {
                setCopyingFromDisk(false);
              }
            }
          }}
        />
      )}
    </div>
  );
};

export default TaskDetailPage;
